import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BadgeTone } from '@/components/ui/badge'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Kredi kartı ekstreleri. Şirket kartı = "Kartlar" hesabına bağlı profil (limit/kesim/son ödeme günü hesap kartında);
 * kişisel kart = personelin kendi kartı (yalnız kendisi görür, muhasebeye yazılmaz). Ekstre kesim gününde worker'da
 * kesilir; şirket kartında faiz + BSMV + KKDF "Kredi Kartı Faiz ve Masrafları" gideri olur.
 */
export const CREDIT_SETTINGS_PERM = 'finance:credit:settings'

export type CardScope = 'company' | 'personal'
export type StatementStatus = 'paid' | 'min_paid' | 'due' | 'overdue'
export type CardTxnKind = 'purchase' | 'cash_advance' | 'fee' | 'refund' | 'payment'

export const STATUS_TONE: Record<StatementStatus, BadgeTone> = { paid: 'success', min_paid: 'info', due: 'warning', overdue: 'danger' }

export type CardStatement = {
  id: string
  cardId: string
  periodStart: string
  statementDate: string
  dueDate: string
  previousBalance: string
  payments: string
  purchases: string
  cashAdvances: string
  fees: string
  refunds: string
  contractInterest: string
  lateInterest: string
  cashAdvanceInterest: string
  bsmv: string
  kkdf: string
  totalDue: string
  minDue: string
  overLimit: string
  futureInstallments: string
  paidSince: string
  status: StatementStatus
  rates: Record<string, string>
  lines: { date: string; kind: CardTxnKind; description: string; amount: string; installment: number; of: number }[]
  interestLines: InterestLine[]
  /** Kesilmemiş dönemin bugüne kadarki ön izlemesi. */
  preview: boolean
}

export type CreditCard = {
  id: string
  scope: CardScope
  moneyAccountId: string
  holderUserId: string
  name: string
  bankName: string
  bankId: string
  bankLogoUrl: string
  last4: string
  limit: string
  statementDay: number
  dueDay: number
  purchaseRate: string | null
  cashAdvanceRate: string | null
  lateRate: string | null
  active: boolean
  ready: boolean
  nextStatementDate: string
  lastStatement: CardStatement | null
}

export type CardTxn = { id: string; date: string; kind: CardTxnKind; amount: string; installments: number; description: string; categoryId: string; locked: boolean }

/** Bireysel kartta dönem borcuna göre oran dilimi; upTo boşsa üst dilim. */
export type RateTier = { upTo: string; purchase: string; late: string }

/** Faiz dökümü satırı: matrah × aylık oran × gün / 30. */
export type InterestLine = { kind: 'contract' | 'late' | 'cash_advance'; base: string; rate: string; from: string; to: string; days: number; amount: string }

export type CreditSettings = {
  scope: CardScope
  validFrom: string
  bsmvRate: string
  kkdfRate: string
  minThreshold: string
  minLowRate: string
  minHighRate: string
  purchaseRate: string
  cashAdvanceRate: string
  lateRate: string
  cashAdvanceLateRate: string
  kmhRate: string
  tiers: RateTier[]
  note: string
  updatedAt: string
  history: CreditSettings[]
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const n = (v: unknown) => Number(v) || 0
const opt = (v: unknown) => (v == null || v === '' ? null : String(v))

function mapStatement(r: Row): CardStatement {
  return {
    id: s(r.id),
    cardId: s(r.card_id),
    periodStart: s(r.period_start),
    statementDate: s(r.statement_date),
    dueDate: s(r.due_date),
    previousBalance: s(r.previous_balance),
    payments: s(r.payments),
    purchases: s(r.purchases),
    cashAdvances: s(r.cash_advances),
    fees: s(r.fees),
    refunds: s(r.refunds),
    contractInterest: s(r.contract_interest),
    lateInterest: s(r.late_interest),
    cashAdvanceInterest: s(r.cash_advance_interest),
    bsmv: s(r.bsmv),
    kkdf: s(r.kkdf),
    totalDue: s(r.total_due),
    minDue: s(r.min_due),
    overLimit: s(r.over_limit),
    futureInstallments: s(r.future_installments),
    paidSince: s(r.paid_since),
    status: (s(r.status) || 'due') as StatementStatus,
    rates: (r.rates && typeof r.rates === 'object' ? r.rates : {}) as Record<string, string>,
    lines: (Array.isArray(r.lines) ? (r.lines as Row[]) : []).map((l) => ({
      date: s(l.date),
      kind: s(l.kind) as CardTxnKind,
      description: s(l.description),
      amount: s(l.amount),
      installment: n(l.installment),
      of: n(l.of),
    })),
    interestLines: (Array.isArray(r.interest_lines) ? (r.interest_lines as Row[]) : []).map((l) => ({
      kind: s(l.kind) as InterestLine['kind'],
      base: s(l.base),
      rate: s(l.rate),
      from: s(l.from),
      to: s(l.to),
      days: n(l.days),
      amount: s(l.amount),
    })),
    preview: r.preview === true,
  }
}

function mapSettings(r: Row): CreditSettings {
  return {
    scope: r.scope === 'company' ? 'company' : 'personal',
    validFrom: s(r.valid_from),
    bsmvRate: s(r.bsmv_rate),
    kkdfRate: s(r.kkdf_rate),
    minThreshold: s(r.min_threshold),
    minLowRate: s(r.min_low_rate),
    minHighRate: s(r.min_high_rate),
    purchaseRate: s(r.purchase_rate),
    cashAdvanceRate: s(r.cash_advance_rate),
    lateRate: s(r.late_rate),
    cashAdvanceLateRate: s(r.cash_advance_late_rate),
    kmhRate: s(r.kmh_rate),
    tiers: (Array.isArray(r.tiers) ? (r.tiers as Row[]) : []).map((x) => ({ upTo: s(x.up_to), purchase: s(x.purchase), late: s(x.late) })),
    note: s(r.note),
    updatedAt: s(r.updated_at),
    history: (Array.isArray(r.history) ? (r.history as Row[]) : []).map(mapSettings),
  }
}

function mapCard(r: Row): CreditCard {
  return {
    id: s(r.id),
    scope: r.scope === 'personal' ? 'personal' : 'company',
    moneyAccountId: s(r.money_account_id),
    holderUserId: s(r.holder_user_id),
    name: s(r.name),
    bankName: s(r.bank_name),
    bankId: s(r.bank_id),
    bankLogoUrl: s(r.bank_logo_url),
    last4: s(r.last4),
    limit: s(r.limit),
    statementDay: n(r.statement_day),
    dueDay: n(r.due_day),
    purchaseRate: opt(r.purchase_rate),
    cashAdvanceRate: opt(r.cash_advance_rate),
    lateRate: opt(r.late_rate),
    active: r.active !== false,
    ready: r.ready === true,
    nextStatementDate: s(r.next_statement_date),
    lastStatement: r.last_statement && typeof r.last_statement === 'object' ? mapStatement(r.last_statement as Row) : null,
  }
}

export const creditKeys = {
  all: ['finance', 'credit'] as const,
  cards: (scope: CardScope) => ['finance', 'credit', 'cards', scope] as const,
  txns: (id: string) => ['finance', 'credit', 'txns', id] as const,
  statements: (id: string) => ['finance', 'credit', 'statements', id] as const,
  statement: (cardId: string, id: string) => ['finance', 'credit', 'statement', cardId, id] as const,
  preview: (cardId: string) => ['finance', 'credit', 'preview', cardId] as const,
  settings: ['finance', 'credit', 'settings'] as const,
}

export const useCreditCards = (scope: CardScope, enabled = true) =>
  useQuery({ queryKey: creditKeys.cards(scope), enabled, queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/credit-cards', { scope })).map(mapCard) })

export const useCardTxns = (id: string | null) =>
  useQuery({
    queryKey: creditKeys.txns(id ?? ''),
    enabled: !!id,
    queryFn: async () =>
      asList(await api.get<Row[]>(`/api/v1/finance/credit-cards/${id}/transactions`)).map((r) => ({
        id: s(r.id),
        date: s(r.date),
        kind: s(r.kind) as CardTxnKind,
        amount: s(r.amount),
        installments: n(r.installments) || 1,
        description: s(r.description),
        categoryId: s(r.category_id),
        locked: r.locked === true,
      })) as CardTxn[],
  })

export const useCardStatements = (id: string | null) =>
  useQuery({ queryKey: creditKeys.statements(id ?? ''), enabled: !!id, queryFn: async () => asList(await api.get<Row[]>(`/api/v1/finance/credit-cards/${id}/statements`)).map(mapStatement) })

export const useCardStatement = (cardId: string, id: string | null) =>
  useQuery({ queryKey: creditKeys.statement(cardId, id ?? ''), enabled: !!id, queryFn: async () => mapStatement(await api.get<Row>(`/api/v1/finance/credit-cards/${cardId}/statements/${id}`)) })

export const useCreditSettings = () =>
  useQuery({ queryKey: creditKeys.settings, queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/credit-settings')).map(mapSettings) })

/** Kesilmemiş dönemin bugüne kadarki ekstre ön izlemesi (faiz dökümüyle). */
export const useCardPreview = (cardId: string | null) =>
  useQuery({
    queryKey: creditKeys.preview(cardId ?? ''),
    enabled: !!cardId,
    queryFn: async () => mapStatement(await api.get<Row>(`/api/v1/finance/credit-cards/${cardId}/preview`)),
  })

/** Kart hareketleri hesap bakiyelerini de etkiler (şirket kartında gider kaydı): tüm finans sorguları tazelenir. */
function useCreditMut<A>(fn: (arg: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }) })
}

export type CardInput = {
  name: string
  bankName: string
  bankId: string
  last4: string
  limit: string
  statementDay: number
  dueDay: number
  holderUserId: string
  purchaseRate: string
  cashAdvanceRate: string
  lateRate: string
}

const rate = (v: string) => (v.trim() ? v.trim().replace(',', '.') : null)

function cardBody(i: CardInput) {
  return {
    name: i.name,
    bank_name: i.bankName,
    bank_id: i.bankId,
    last4: i.last4,
    limit: i.limit || '0',
    statement_day: i.statementDay,
    due_day: i.dueDay,
    holder_user_id: i.holderUserId,
    purchase_rate: rate(i.purchaseRate),
    cash_advance_rate: rate(i.cashAdvanceRate),
    late_rate: rate(i.lateRate),
  }
}

/** Kişisel kart (id yoksa yeni) ya da şirket kartı profili (moneyAccountId ile). */
export const useSaveCard = () =>
  useCreditMut(({ id, moneyAccountId, input }: { id?: string; moneyAccountId?: string; input: CardInput }) =>
    moneyAccountId
      ? api.put(`/api/v1/finance/credit-cards/company/${moneyAccountId}`, cardBody(input))
      : id
        ? api.put(`/api/v1/finance/credit-cards/${id}`, cardBody(input))
        : api.post('/api/v1/finance/credit-cards', cardBody(input)),
  )

export type CardTxnInput = { date: string; kind: CardTxnKind; amount: string; installments: number; description: string; categoryId: string }

export const useAddCardTxn = (cardId: string) =>
  useCreditMut((i: CardTxnInput) =>
    api.post(`/api/v1/finance/credit-cards/${cardId}/transactions`, {
      date: i.date,
      kind: i.kind,
      amount: i.amount,
      installments: i.installments,
      description: i.description,
      category_id: i.categoryId,
    }),
  )

export const useVoidCardTxn = (cardId: string) => useCreditMut((txnId: string) => api.del(`/api/v1/finance/credit-cards/${cardId}/transactions/${txnId}`))

export const useIssueDueStatements = () => useCreditMut(() => api.post<Row>('/api/v1/finance/credit-cards/issue-due', {}))

export const useSaveCreditSettings = () =>
  useCreditMut((st: CreditSettings) =>
    api.put(`/api/v1/finance/credit-settings/${st.scope}`, {
      valid_from: st.validFrom,
      note: st.note,
      cash_advance_late_rate: (st.cashAdvanceLateRate || '0').replace(',', '.'),
      kmh_rate: (st.kmhRate || '0').replace(',', '.'),
      tiers: st.tiers.map((x) => ({ up_to: x.upTo.trim() ? x.upTo.replace(',', '.') : null, purchase: x.purchase.replace(',', '.'), late: x.late.replace(',', '.') })),
      bsmv_rate: st.bsmvRate.replace(',', '.'),
      kkdf_rate: st.kkdfRate.replace(',', '.'),
      min_threshold: st.minThreshold.replace(',', '.'),
      min_low_rate: st.minLowRate.replace(',', '.'),
      min_high_rate: st.minHighRate.replace(',', '.'),
      purchase_rate: st.purchaseRate.replace(',', '.'),
      cash_advance_rate: st.cashAdvanceRate.replace(',', '.'),
      late_rate: st.lateRate.replace(',', '.'),
    }),
  )
